import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const PortalUploads = () => {
    const [uploads, setUploads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedUpload, setSelectedUpload] = useState(null);
    const [showReviewModal, setShowReviewModal] = useState(false);
    const [filters, setFilters] = useState({
        status: 'PENDING'
    });
    const [reviewForm, setReviewForm] = useState({
        status: 'APPROVED',
        review_notes: '',
        file_to_case: false
    });

    useEffect(() => {
        fetchUploads();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchUploads = async () => {
        try {
            const token = localStorage.getItem('access_token');
            let url = '/api/portal/manage/uploads/';

            const params = new URLSearchParams();
            if (filters.status) params.append('status', filters.status);
            if (params.toString()) url += '?' + params.toString();

            const response = await fetch(url, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            setUploads(Array.isArray(data) ? data : data.results || []);
        } catch (err) {
            console.error('Error fetching uploads:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleReview = async (e) => {
        e.preventDefault();
        if (!selectedUpload) return;

        try {
            const token = localStorage.getItem('access_token');
            const response = await fetch(`/api/portal/manage/uploads/${selectedUpload.id}/review/`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(reviewForm)
            });

            if (!response.ok) throw new Error('Failed to review upload');

            setShowReviewModal(false);
            setSelectedUpload(null);
            setReviewForm({ status: 'APPROVED', review_notes: '', file_to_case: false });
            fetchUploads();
        } catch (err) {
            alert(err.message);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'PENDING': return 'bg-yellow-100 text-yellow-800';
            case 'APPROVED': return 'bg-green-100 text-green-800';
            case 'REJECTED': return 'bg-red-100 text-red-800';
            case 'FILED': return 'bg-blue-100 text-blue-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const formatFileSize = (bytes) => {
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="p-6 bg-gray-50 min-h-screen">
            {/* Header */}
            <div className="mb-6">
                <nav className="text-sm text-gray-500 mb-2">
                    <Link to="/portal" className="hover:text-blue-600">Client Portal</Link>
                    <span className="mx-2">›</span>
                    <span>Client Uploads</span>
                </nav>
                <h1 className="text-2xl font-bold text-gray-900">Client Uploads</h1>
                <p className="text-gray-600 mt-1">Review and approve documents uploaded by clients</p>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                        <select
                            value={filters.status}
                            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2"
                        >
                            <option value="">All Statuses</option>
                            <option value="PENDING">Pending Review</option>
                            <option value="APPROVED">Approved</option>
                            <option value="REJECTED">Rejected</option>
                            <option value="FILED">Filed</option>
                        </select>
                    </div>
                    <div className="flex items-end">
                        <button
                            onClick={fetchUploads}
                            className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                        >
                            Apply Filters
                        </button>
                    </div>
                </div>
            </div>

            {/* Uploads Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {uploads.length > 0 ? (
                    uploads.map((upload) => (
                        <div
                            key={upload.id}
                            className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden"
                        >
                            <div className="p-6">
                                <div className="flex items-start justify-between mb-4">
                                    <div className="p-3 bg-gray-100 rounded-lg">
                                        <svg className="w-8 h-8 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                    </div>
                                    <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(upload.status)}`}>
                                        {upload.status_display}
                                    </span>
                                </div>

                                <h3 className="font-semibold text-gray-900 mb-1 truncate">{upload.title}</h3>
                                <p className="text-sm text-gray-500 mb-3">
                                    Uploaded by: {upload.client_name}
                                </p>

                                {upload.description && (
                                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">{upload.description}</p>
                                )}

                                <div className="flex items-center justify-between text-sm text-gray-500">
                                    <span>{formatFileSize(upload.file_size)}</span>
                                    <span>{new Date(upload.uploaded_at).toLocaleDateString()}</span>
                                </div>

                                {upload.case_title && (
                                    <p className="text-sm text-blue-600 mt-2">
                                        Related to: {upload.case_title}
                                    </p>
                                )}
                            </div>

                            <div className="px-6 py-3 bg-gray-50 border-t border-gray-100 flex gap-2">
                                <a
                                    href={upload.file}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex-1 text-center px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg"
                                >
                                    View File
                                </a>
                                {upload.status === 'PENDING' && (
                                    <button
                                        onClick={() => {
                                            setSelectedUpload(upload);
                                            setShowReviewModal(true);
                                        }}
                                        className="flex-1 px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                                    >
                                        Review
                                    </button>
                                )}
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="col-span-3 bg-white rounded-xl p-12 text-center text-gray-500">
                        <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                        <p>No uploads found</p>
                    </div>
                )}
            </div>

            {/* Review Modal */}
            {showReviewModal && selectedUpload && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4">
                        <div className="p-6 border-b border-gray-200">
                            <div className="flex items-center justify-between">
                                <h2 className="text-xl font-semibold text-gray-900">Review Upload</h2>
                                <button onClick={() => setShowReviewModal(false)} className="text-gray-400 hover:text-gray-600">
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        </div>

                        <div className="p-6 border-b border-gray-100">
                            <p className="font-medium text-gray-900">{selectedUpload.title}</p>
                            <p className="text-sm text-gray-500">From: {selectedUpload.client_name}</p>
                            {selectedUpload.description && (
                                <p className="text-sm text-gray-600 mt-2">{selectedUpload.description}</p>
                            )}
                        </div>

                        <form onSubmit={handleReview} className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Decision</label>
                                <select
                                    value={reviewForm.status}
                                    onChange={(e) => setReviewForm({ ...reviewForm, status: e.target.value })}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                >
                                    <option value="APPROVED">Approve</option>
                                    <option value="REJECTED">Reject</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Review Notes</label>
                                <textarea
                                    value={reviewForm.review_notes}
                                    onChange={(e) => setReviewForm({ ...reviewForm, review_notes: e.target.value })}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                    rows="3"
                                    placeholder="Optional notes about this review"
                                ></textarea>
                            </div>

                            {reviewForm.status === 'APPROVED' && selectedUpload.case && (
                                <label className="flex items-center">
                                    <input
                                        type="checkbox"
                                        checked={reviewForm.file_to_case}
                                        onChange={(e) => setReviewForm({ ...reviewForm, file_to_case: e.target.checked })}
                                        className="rounded border-gray-300 text-blue-600"
                                    />
                                    <span className="ml-2 text-sm text-gray-700">
                                        File as official case document
                                    </span>
                                </label>
                            )}

                            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                                <button
                                    type="button"
                                    onClick={() => setShowReviewModal(false)}
                                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className={`px-4 py-2 rounded-lg text-white ${reviewForm.status === 'REJECTED'
                                        ? 'bg-red-600 hover:bg-red-700'
                                        : 'bg-green-600 hover:bg-green-700'
                                        }`}
                                >
                                    {reviewForm.status === 'REJECTED' ? 'Reject' : 'Approve'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PortalUploads;
